import { joiResolver } from '@hookform/resolvers/joi';

import { makeStyles } from 'tss-react/mui';
import { SelectChangeEvent } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import {
  ApiButton,
  Box,
  ChangeRequestDialog,
  ChangeRequestForm,
  ContentTemplate,
  CustomSelect,
  ErrorTemplate,
  ErrorTemplateCode,
  Grid,
  HeaderInput,
  HeaderTemplate,
  prepareFiles,
  Progress,
  SingleSelectionRadio,
  TitleHeader,
  Typography,
  useSnackbarHandler,
} from 'web-library';
import { AUTHORITY_NAMES, FUNCTION_NAMES } from 'web-library/models/AuthorityFunctions';
import { REQUEST_TYPES } from 'web-library/models/Request';
import { UploadedFile } from 'web-library/models/UploadedFile';
import editGroupAccessConfigurationSchema from 'web-library/models/UserGroupAccessConfiguration/editGroupAccessConfigurationSchema';
import {
  AccessConfigDetails,
  Authority,
  FunctionModule,
  InsertDeleteListObject,
  TargetRoleMode,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import TransactionAuditLogUpdatedDetails from 'web-library/components/TransactionAuditLogUpdatedDetails';
import { useGetAccessConfiguration } from '../../../api-refactored/user-group-access-configuration/useGetAccessConfiguration';
import { useGetRequestFileConfigs } from '../../../api-refactored/user-group-access-configuration/useGetRequestFileConfigs';
import { usePostAccessConfig } from '../../../api-refactored/user-group-access-configuration/usePostAccessConfig';
import { usePostRequestFilePresigned } from '../../../api-refactored/user-group-access-configuration/usePostRequestFilePresigned';
import { usePostSearchApprovers } from '../../../api-refactored/user-group-access-configuration/usePostSearchApprovers';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';
import { Institutions } from '../../../api/user/model';
import { RequestFileDropZone } from '../../../components/access-configuration/RequestFileDropZone';
import TransactionLogModal from '../../../components/TransactionLogModal';
import { mapApproversToDropdown } from '../../userGroupAccessConfiguration/CreateEditViewUserGroup';
import ErrorPageTitle from '../../../components/errors/ErrorPageTitle';
import AccessConfigurationListingDataTable from './AccessConfigurationListingDataTable';

const useStyles = makeStyles()((theme) => ({
  approverRemarksField: {
    width: '63.5%',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  datatable: {
    marginBottom: theme.spacing(8),
  },
  changeRequest: {
    marginTop: theme.spacing(-2),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
  confirmChanges: {
    marginBottom: theme.spacing(7),
    height: 'fit-content',
  },
}));

interface SelectedInstitution {
  key: string;
  value: string;
}

const insertGCELevelsAccess = (
  list: InsertDeleteListObject[],
  authorityData: Authority,
  userType: TargetRoleMode,
  selectedInstitution: SelectedInstitution
) => {
  if (authorityData.accessOptions['GCE A-Level']) {
    list.push({
      organisationUuid: selectedInstitution.value,
      accessOptionUuid: authorityData.accessOptions['GCE A-Level'],
      authorityUuid: authorityData.authorityUuid,
      userType,
    });
  }

  if (authorityData.accessOptions['GCE N(A)-Level']) {
    list.push({
      organisationUuid: selectedInstitution.value,
      accessOptionUuid: authorityData.accessOptions['GCE N(A)-Level'],
      authorityUuid: authorityData.authorityUuid,
      userType,
    });
  }

  if (authorityData.accessOptions['GCE O-Level']) {
    list.push({
      organisationUuid: selectedInstitution.value,
      accessOptionUuid: authorityData.accessOptions['GCE O-Level'],
      authorityUuid: authorityData.authorityUuid,
      userType,
    });
  }

  if (authorityData.accessOptions['GCE N(T)-Level']) {
    list.push({
      organisationUuid: selectedInstitution.value,
      accessOptionUuid: authorityData.accessOptions['GCE N(T)-Level'],
      authorityUuid: authorityData.authorityUuid,
      userType,
    });
  }
};

const constructList = (
  accessConfigData: AccessConfigDetails,
  selectedInstitution: SelectedInstitution,
  accessOption: string[][],
  userType: TargetRoleMode
): [InsertDeleteListObject[], InsertDeleteListObject[]] => {
  const insertList: InsertDeleteListObject[] = [];
  const deleteList: InsertDeleteListObject[] = [];

  const accessMatrixOption =
    accessConfigData.accessMatrixConfigurations[selectedInstitution.key]?.accessMatrix;

  Object.keys(accessConfigData.authorities)
    .sort() // NOSONAR
    .forEach((authorityName, index) => {
      const authorityData = accessConfigData.authorities[authorityName];

      if (accessOption[index]?.length > 0) {
        accessOption[index].forEach((accessOptionUuid) => {
          if (accessOptionUuid === 'gceLevelsUuid') {
            insertGCELevelsAccess(insertList, authorityData, userType, selectedInstitution);
          } else {
            insertList.push({
              organisationUuid: selectedInstitution.value,
              accessOptionUuid,
              authorityUuid: authorityData.authorityUuid,
              userType,
            });
          }
        });
      }

      if (accessMatrixOption?.[authorityName] && accessMatrixOption?.[authorityName].length > 0) {
        accessMatrixOption?.[authorityName].forEach((accessOptionName) =>
          deleteList.push({
            organisationUuid: selectedInstitution.value,
            accessOptionUuid: authorityData.accessOptions[accessOptionName],
            authorityUuid: authorityData.authorityUuid,
            userType,
          })
        );
      }
    });

  return [insertList, deleteList];
};

const UserAccessConfigurationWithFunctionList = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [targetRoleMode, setTargetRoleMode] = useState<TargetRoleMode>(TargetRoleMode.Candidate);

  const [openTransactionLogModal, setOpenTransactionLogModal] = useState<boolean>(false);

  const [files, setFiles] = useState<UploadedFile[]>([]);

  const [confirmChangesModal, setConfirmChangesModal] = useState<boolean>(false);

  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);

  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  const [selectedAccessOption, setSelectedAccessOption] = useState<string[][]>([]);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [uploadFileErrorMessage, setUploadFileErrorMessage] = useState<string>('');
  const [requestType, setRequestType] = useState(REQUEST_TYPES.CANDIDATE_ACCESS_CONFIG);

  const formHook = useForm({
    mode: 'onSubmit',
    resolver: joiResolver(editGroupAccessConfigurationSchema()),
  });

  const { handleSubmit, getValues } = formHook;
  const { data = [] } = useGetUserAuthorities();

  const [institutionList, setInstitutionList] = useState<
    { key: string; value: ReactNode; children: string }[]
  >([]);

  const [selectedInstitution, setSelectedInstitution] = useState<SelectedInstitution>({
    key: '',
    value: '',
  });

  const [isNoAccess, setIsNoAccess] = useState<boolean>(false);

  const hasEditPermission = data.includes(AUTHORITY_NAMES.CP_ADMIN_USER_ACCESS_CONFIGURATION_EDIT);

  const mapInstitutionToDropdown = (institutionData: Institutions) => {
    if (institutionData === undefined) {
      return [];
    }

    const array: { key: string; value: unknown; children: string }[] = [];

    Object.entries(institutionData)
      .sort() // NOSONAR
      .forEach((entry) => {
        const [key, value] = entry;
        array.push({
          key,
          value,
          children: key,
        });
      });

    return array;
  };

  const {
    mutate: mutatePostSearchApprovers,
    data: postSearchApproversData,
    isLoading: postSearchApproversIsLoading,
  } = usePostSearchApprovers({});

  const { data: requestFilesConfigData } = useGetRequestFileConfigs({});

  useEffect(() => {
    mutatePostSearchApprovers({
      functionName: FUNCTION_NAMES.ADMIN_USER_GROUP,
      functionModule: FunctionModule.ADMIN,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutatePostSearchApprovers]);

  const {
    data: accessConfigurationListingData,
    isFetching: isLoadingAccessConfiguration,
  } = useGetAccessConfiguration({
    targetRole: targetRoleMode,
    onSuccess: (res) => {
      const defaultSelectedInstitution = Object.entries(res.institutions).sort()[0]; // NOSONAR
      setSelectedInstitution({
        key: (defaultSelectedInstitution && defaultSelectedInstitution[0]) || '',
        value: (defaultSelectedInstitution && defaultSelectedInstitution[1]) || '',
      });
      setInstitutionList(
        mapInstitutionToDropdown(res.institutions) as {
          key: string;
          value: ReactNode;
          children: string;
        }[]
      );

      if (defaultSelectedInstitution) {
        const pendingRequest =
          res.accessMatrixConfigurations[defaultSelectedInstitution[0]].hasPendingRequest;

        handleSetHasPendingRequest(pendingRequest);
      }
    },
    onError: (err) => {
      if (err?.response?.data.code === '1007') {
        setIsNoAccess(true);
      }
    },
  });

  const { snackbarHandler } = useSnackbarHandler();

  const { mutate: mutatePostAccessConfig } = usePostAccessConfig({
    onSuccess: () => {
      setShouldSubmit(false);
      setApiButtonStatus('success');
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>UserGroups.ChangeRequestSuccessfullySentForApproval'),
      });

      if (
        accessConfigurationListingData &&
        accessConfigurationListingData.accessMatrixConfigurations &&
        accessConfigurationListingData.accessMatrixConfigurations[selectedInstitution.key]
      ) {
        accessConfigurationListingData.accessMatrixConfigurations[
          selectedInstitution.key
        ].hasPendingRequest = true;
      }

      setHasPendingRequest(true);

      setFiles([]);
    },
    onError: (error) => {
      setShouldSubmit(false);
      setApiButtonStatus('error');
      setErrorMessage(error?.response?.data?.message!);
    },
  });

  const { mutate: postRequestFilePresigned } = usePostRequestFilePresigned({
    onSuccess: (fileDataContent) => {
      const tempFiles: UploadedFile[] = [];
      fileDataContent.forEach((_value, index) => {
        const pendingFile = files[index];
        const fileData = fileDataContent[index];
        pendingFile.presignedUrl = fileData.presignedUrl;
        pendingFile.key = fileData.key;
        tempFiles.push(pendingFile);
      });
      setFiles(tempFiles);
    },
    onError: (err) => {
      setUploadFileErrorMessage(err?.response?.data.message!);
      setApiButtonStatus('error');
    },
  });

  useEffect(() => {
    const isAllFilesUploaded = files.every((f) => f.uploaded);
    const fileUploadHasError = files.some((f) => f.hasError);
    const fileKeys: string[] = [''];
    files.forEach((f) => {
      if (f.key) {
        fileKeys.push(f.key);
      }
    });

    if (fileUploadHasError) {
      setApiButtonStatus('error');
      setShouldSubmit(false);
    }

    if (isAllFilesUploaded && shouldSubmit && accessConfigurationListingData) {
      const [insertList, deleteList] = constructList(
        accessConfigurationListingData,
        selectedInstitution,
        selectedAccessOption,
        targetRoleMode
      );

      const postAccessConfigPayload = {
        updateOrganisationAccessRequest: {
          insertList,
          deleteList,
        },
        requestDetail: {
          requestorRemarks: getValues('remarks'),
          approverUuid: getValues('approver')?.code,
          fileKeys,
        },
      };
      mutatePostAccessConfig(postAccessConfigPayload);
    }
  }, [
    accessConfigurationListingData,
    files,
    getValues,
    mutatePostAccessConfig,
    selectedAccessOption,
    selectedInstitution,
    selectedInstitution.key,
    shouldSubmit,
    targetRoleMode,
    t,
  ]);

  const handleOpenTransactionLogModal = () => setOpenTransactionLogModal(true);

  const handleOpenConfirmChangesModal = () => {
    if (errorMessage || uploadFileErrorMessage) {
      setConfirmChangesModal(false);
    } else {
      setConfirmChangesModal(true);
    }
  };

  const handleSetHasPendingRequest = (pendingRequest: boolean) => {
    setHasPendingRequest(pendingRequest);

    if (pendingRequest) {
      setErrorMessage(t('admin->>UserGroups.AccountPendingRequestError'));
    } else {
      setErrorMessage('');
    }
  };

  const handleChangeInstitution = (e: SelectChangeEvent<unknown>) => {
    const selectedInstitutionObject = (institutionList || []).find(
      (institution) => institution.value === e.target.value
    );
    const selectedInstitutionKey = selectedInstitutionObject?.key || '';

    setSelectedInstitution({
      key: selectedInstitutionKey,
      value: e.target.value as string,
    });

    if (
      accessConfigurationListingData &&
      accessConfigurationListingData.accessMatrixConfigurations &&
      accessConfigurationListingData.accessMatrixConfigurations[selectedInstitutionKey]
    ) {
      handleSetHasPendingRequest(
        accessConfigurationListingData.accessMatrixConfigurations[selectedInstitutionKey]
          .hasPendingRequest
      );
    }
    setSelectedAccessOption([]);
  };

  const handleCheckRequestAccessConfig = () => {
    const { filenames } = prepareFiles(files, setFiles);
    setShouldSubmit(true);
    setApiButtonStatus('loading');
    setConfirmChangesModal(false);

    if (filenames.length) {
      postRequestFilePresigned({
        filenames,
      });
    }
  };

  if (
    isLoadingAccessConfiguration ||
    postSearchApproversIsLoading ||
    (institutionList.length === 0 && !isNoAccess)
  ) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <form onSubmit={handleSubmit(() => handleOpenConfirmChangesModal())}>
      <TitleHeader title={t('admin->>UserGroups.AccessConfiguration')} />
      <HeaderTemplate>
        <HeaderInput
          label={t('admin->>UserGroups.TargetRole')}
          input={
            <SingleSelectionRadio
              data={[
                {
                  label: t('admin->>UserGroups.Candidate'),
                  value: TargetRoleMode.Candidate,
                },
                {
                  label: t('admin->>UserGroups.Coordinator'),
                  value: TargetRoleMode.Coordinator,
                },
              ]}
              onChange={(event) => {
                const targetRole = event.target.value as TargetRoleMode;
                setIsNoAccess(false);
                setTargetRoleMode(targetRole);
                if (targetRole === TargetRoleMode.Coordinator) {
                  setRequestType(REQUEST_TYPES.COORDINATOR_ACCESS_CONFIG);
                } else {
                  setRequestType(REQUEST_TYPES.CANDIDATE_ACCESS_CONFIG);
                }
              }}
              value={targetRoleMode}
            />
          }
          topBottom={false}
        />
      </HeaderTemplate>

      <HeaderTemplate>
        <HeaderInput
          label={t('admin->>UserGroups.Institution')}
          input={
            <CustomSelect
              id="inputSelect-Institution"
              isHeader
              menuItems={institutionList}
              defaultValue={selectedInstitution.value || (institutionList[0] || {}).value}
              onChange={handleChangeInstitution}
            />
          }
          topBottom={false}
        />
      </HeaderTemplate>
      {isNoAccess ? (
        <ContentTemplate type="full">
          <ErrorTemplate
            errorCode={'generic' as ErrorTemplateCode}
            title={
              <ErrorPageTitle
                label={t(
                  'components->>GenericError.We’re sorry, but you do not have access to this feature.'
                )}
              />
            }
            subtitle=""
          />
        </ContentTemplate>
      ) : (
        <>
          <ContentTemplate isContinuable isDividerBottom type="full">
            <Box className={classes.datatable}>
              {accessConfigurationListingData && (
                <AccessConfigurationListingDataTable
                  accessConfigurationListingData={accessConfigurationListingData}
                  selectedInstitution={selectedInstitution}
                  selectedAccessOption={selectedAccessOption}
                  setSelectedAccessOption={setSelectedAccessOption}
                  targetRoleMode={targetRoleMode}
                  isEditable={!hasPendingRequest}
                />
              )}
            </Box>
          </ContentTemplate>
          <ContentTemplate continuous type="full">
            <ChangeRequestForm
              formHook={formHook}
              approverDropdowns={mapApproversToDropdown(postSearchApproversData)}
              isEditable={!hasPendingRequest}
            />
            {requestFilesConfigData && (
              <Grid container spacing={0}>
                <Grid md={6} sm={12} xs={12}>
                  <RequestFileDropZone
                    fileConfig={requestFilesConfigData}
                    files={files}
                    setFiles={setFiles}
                    isLoading={apiButtonStatus === 'loading'}
                    isEditable={!hasPendingRequest}
                  />
                </Grid>
              </Grid>
            )}
            <TransactionAuditLogUpdatedDetails
              updatedAt={
                accessConfigurationListingData?.accessMatrixConfigurations[selectedInstitution.key]
                  ?.updatedAt as string
              }
              updatedBy={
                accessConfigurationListingData?.accessMatrixConfigurations[selectedInstitution.key]
                  ?.updatedBy as string
              }
              handleOpenLogModal={handleOpenTransactionLogModal}
              label={t('admin->>UserGroups.ViewTransactionLog')}
            />
            {errorMessage && (
              <Typography className={classes.errorText} variant="body2">
                {errorMessage}
              </Typography>
            )}
            {uploadFileErrorMessage && (
              <Typography className={classes.errorText} variant="body2">
                {uploadFileErrorMessage}
              </Typography>
            )}
            {hasEditPermission && (
              <ApiButton
                status={apiButtonStatus}
                className={classes.confirmChanges}
                id="button-confirm-changes-access-config"
                type="submit"
                disabled={hasPendingRequest}
              >
                {t('admin->>UserGroups.ConfirmChanges')}
              </ApiButton>
            )}
            <TransactionLogModal
              modalState={openTransactionLogModal}
              setOpenModalState={setOpenTransactionLogModal}
              institutionUuid={selectedInstitution.value}
              requestType={requestType}
            />
            <ChangeRequestDialog
              open={confirmChangesModal}
              onClose={() => setConfirmChangesModal(false)}
              onConfirm={handleCheckRequestAccessConfig}
            />
          </ContentTemplate>
        </>
      )}
    </form>
  );
};

export default UserAccessConfigurationWithFunctionList;
